'use client';

import React from 'react';
import styled from 'styled-components';
import { Text } from '@lmn/design-system';
import type { Brand } from '@/domainLogic/index.ts';

import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import { useIsMobile } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useFetchHotelPromotions } from '@/app/components/hotel-detail/hooks/useFetchHotelPromotions';
import LmnDealsExtras from '@/app/components/hotel-detail/LmnDealsExtras';
import { LmnDealMessage } from '@/app/components/hotel-detail/LmnDealMessage';
import { LmnDealBadge } from '@/app/components/hotel-detail/LmnDealBadge';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import { useTranslation } from '@/app/components/translations/Translation';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Separator = styled.div`
  height: 1px;
  background-color: ${(p) => p.theme.commons.colors.gray100};
`;

const PerksSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LmnDealExtrasList = styled.ul`
  list-style: none;
  padding-left: 0;

  li {
    position: relative;
    padding-left: 24px;

    &::before {
      content: '•';
      position: absolute;
      left: 8px;
      font-size: 1em;
      line-height: 1;
      top: 0.3em;
    }
  }
`;

export interface LmnDealInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenByBrowserHistory?: () => void;
}

export function LmnDealInfoModal(props: LmnDealInfoModalProps) {
  const { isOpen, onClose, onOpenByBrowserHistory } = props;

  const { t } = useTranslation();

  const { localization, commonData } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { data: promotions } = useFetchHotelPromotions();
  const { promotionAddedValues } = promotions || {};
  const { imageBasePath } = localization;

  return (
    <OpenXModal
      isOpen={isOpen ?? false}
      isMobile={isMobile ?? true}
      brand={localization.brand as Brand}
      onClose={onClose}
      onCloseByBrowserHistory={onClose}
      onOpenByBrowserHistory={onOpenByBrowserHistory}
      fullScreen={false}
      className="modal-large"
      id="lmn-deal-info-modal"
      aria-label={t('vacation.select.cc.lmdeal.modal.title')}
      bodyBackgroundColor="gray0"
      bodyPadding={0}
      bodyPaddingS={0}
      bodyPaddingXS={0}
      scrollShadows={localization?.configValuesApp?.evolutionEnabled}
      header={{
        sticky: true,
        element: (
          <ModalHeader removeShadow={true} handleClose={onClose}>
            <img
              src={`${imageBasePath}icons/last_minute_deals_rebrand.svg`}
              alt=""
              width={20}
              height={20}
              css={`
                margin-right: 4px;
              `}
            />
            {t('vacation.select.cc.lmdeal.modal.title')}
          </ModalHeader>
        ),
      }}
    >
      <ModalContent>
        <SectionContent>
          <LmnDealBadge showInfo={false} />
          <LmnDealExtrasList>
            <li>
              <TextBase>{t('vacation.select.cc.lmdeals.extras.badge_explanation')}</TextBase>
            </li>
            {promotions?.bookDate && (
              <li>
                <TextBase>
                  <LmnDealMessage
                    bookDate={promotions.bookDate}
                    checkin={promotions.checkin}
                    checkout={promotions.checkout}
                  />
                </TextBase>
              </li>
            )}
          </LmnDealExtrasList>
        </SectionContent>

        <Separator />

        <SectionContent>
          <PerksSection>
            {promotionAddedValues && promotionAddedValues.length > 0 ? (
              <>
                <Text variant="Title4">{t('vacation.select.cc.lmdeals.extras.title')}</Text>
                <LmnDealsExtras extras={promotionAddedValues} />
              </>
            ) : undefined}
          </PerksSection>
        </SectionContent>
      </ModalContent>
    </OpenXModal>
  );
}
